import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Calendar, MapPin,
  Settings, Shield, Loader2, Tag, ClipboardList
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';

interface AdminUser {
  id: string;
  role: 'admin' | 'moderator' | 'user';
}

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/posts', icon: FileText, label: 'Posts' },
  { to: '/admin/events', icon: Calendar, label: 'Events' },
  { to: '/admin/places', icon: MapPin, label: 'Places' },
  { to: '/admin/deals', icon: Tag, label: 'Deals' },
  { to: '/admin/claims', icon: ClipboardList, label: 'Claims' },
  { to: '/admin/integrations', icon: Settings, label: 'Integrations' },
];

export default function AdminLayout() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (authLoading) return;

      const token = localStorage.getItem('auth_token');

      if (!user || !token) {
        navigate('/login?redirect=' + encodeURIComponent(location.pathname));
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.status === 403) {
          setError('You do not have permission to access the admin panel.');
          setLoading(false);
          return;
        }

        if (response.status === 401) {
          navigate('/login?redirect=' + encodeURIComponent(location.pathname));
          return;
        }

        if (response.ok) {
          setAdminUser({ id: user.id, role: 'admin' });
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking admin access:', err);
        setError('Failed to verify admin access.');
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [user, authLoading, navigate, location.pathname]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-coral-500 text-white rounded-xl hover:bg-coral-600 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Admin Top Navigation */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-16 z-40">
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-2 h-12 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 pr-3 border-r dark:border-slate-700 flex-shrink-0">
              <Shield className="w-4 h-4 text-coral-500" />
              <span className="font-semibold text-gray-900 dark:text-white text-sm">Admin</span>
            </div>
            <nav className="flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-coral-50 dark:bg-coral-900/20 text-coral-600 dark:text-coral-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
